// ============================================================================
// "Download PDF" for the contents modal — the loading sheet that rides with the
// truck. It prints exactly what the modal shows, in the same order: the booking's
// route/customer header, then each ORDER group (lead id + client), and under it
// every PO with its item lines. Nothing is summarised away — the person holding
// this sheet at the dock is checking boxes against it line by line.
// ============================================================================
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtIST, fmtQtyUnit, fmtText } from "../../format";
import { LogisticsOrderRow } from "../../types";
import { AllocationGroup } from "./allocation_groups";

const BRAND: [number, number, number] = [36, 170, 183]; // colorPrimary #24AAB7
const MARGIN = 14;
const PAGE_BOTTOM = 282; // A4 portrait is 297mm tall — leave room for the footer

// Move the cursor onto a new page when `needed` mm of content would not fit.
const ensureSpace = (doc: jsPDF, y: number, needed: number): number =>
  y + needed > PAGE_BOTTOM ? (doc.addPage(), MARGIN + 6) : y;

const lastY = (doc: jsPDF, fallback: number): number =>
  (doc as any).lastAutoTable?.finalY ?? fallback;

// One item line, printed as it reads on screen: product name, then the private-label
// identity and SKU underneath.
const productCell = (it: AllocationGroup["allocations"][number]["items"][number]) =>
  [
    it.product?.product_name || "-",
    it.product?.private_label_name ? `Private label: ${it.product.private_label_name}` : "",
    it.product?.sku ? `SKU ${it.product.sku}` : "",
  ]
    .filter(Boolean)
    .join("\n");

export function downloadAllocationItemsPdf(
  booking: LogisticsOrderRow,
  groups: AllocationGroup[]
): void {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // ── Header: who / where, so the sheet stands on its own away from the screen ──
  doc.setFontSize(15);
  doc.setTextColor(20);
  doc.text("Transfer contents", MARGIN, 16);

  const poCount = groups.reduce((n, g) => n + g.allocations.length, 0);
  const meta: string[] = [
    `Booking #${booking.id}${booking.provider_ref ? ` · ${booking.provider_ref}` : ""}`,
    // ASCII only in the PDF text: jsPDF's built-in Helvetica is WinAnsi, so an arrow (or any
    // other non-Latin-1 glyph) comes out as garbage and mangles the whole line's spacing.
    `Route: ${booking.origin_label || "-"} -> ${booking.destination_label || "-"}`,
    // The booking's own customer is one of MANY on a consolidated truck, so naming it at the top of
    // a 21-order manifest would read as "this sheet is theirs". Each order names itself on its band.
    ...(groups.length > 1
      ? []
      : [
          `Customer: ${booking.client_name || "-"}${
            booking.client_contact ? ` · ${booking.client_contact}` : ""
          }`,
        ]),
    `Carrier: ${fmtText(booking.provider_type) || "-"}${
      booking.vehicle_number ? ` · ${booking.vehicle_number}` : ""
    }${booking.driver_name ? ` · ${booking.driver_name}` : ""}`,
    `${poCount} PO${poCount === 1 ? "" : "s"} across ${groups.length} order${
      groups.length === 1 ? "" : "s"
    } · dispatch ${fmtIST(booking.target_dispatch_start, "DD MMM")}`,
  ];
  doc.setFontSize(9);
  doc.setTextColor(110);
  meta.forEach((line, i) => doc.text(line, MARGIN, 23 + i * 4.6));

  let y = 23 + meta.length * 4.6 + 4;

  // ── Body: order group → PO → item lines ─────────────────────────────────────
  groups.forEach((group) => {
    y = ensureSpace(doc, y, 26); // never orphan a group heading at the page foot
    doc.setFillColor(...BRAND);
    doc.rect(MARGIN, y - 4.5, 182, 7, "F");
    doc.setFontSize(10);
    doc.setTextColor(255);
    doc.text(
      `${group.label}  (${group.allocations.length} PO${
        group.allocations.length === 1 ? "" : "s"
      })`,
      MARGIN + 2,
      y
    );
    y += 6;

    group.allocations.forEach((alloc) => {
      const heading = [
        alloc.po_number || "PO -",
        alloc.supplier_name || "",
        alloc.shipment_type ? `[${fmtText(alloc.shipment_type)}]` : "",
        // The transfer order this PO rides, as the screen shows it — the number the dock and Zoho
        // both work off, so a printed sheet without it cannot be reconciled against either.
        alloc.transfer_order_number || "",
      ]
        .filter(Boolean)
        .join("   ");

      autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN },
        head: [[heading, "Qty"]],
        body: alloc.items?.length
          ? alloc.items.map((it) => [productCell(it), fmtQtyUnit(it.qty, it.unit)])
          : [["No item lines on this allocation.", ""]],
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak", textColor: 40 },
        headStyles: { fillColor: [245, 245, 245], textColor: 40, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 150 }, 1: { cellWidth: 32, halign: "right" } },
      });
      y = lastY(doc, y) + 4;
    });

    y += 3;
  });

  // ── Footer: page numbers + when it was printed (a stale sheet is a real hazard) ──
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Generated ${dayjs().format("DD MMM YYYY, hh:mm A")} · page ${p} of ${pages}`,
      MARGIN,
      290
    );
  }

  const stamp = dayjs().format("YYYY-MM-DD");
  const name = booking.lead_id
    ? `transfer-contents-${booking.lead_id}-${stamp}.pdf`
    : `transfer-contents-booking-${booking.id}-${stamp}.pdf`;
  doc.save(name);
}
